import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Sticky "acknowledge me" toasts: "New on the GOAT Blog" (#15794), "Message
// from GOAT" (#16746) and the promo toast (#16852). Rides on the same element as gl-toast
// (shared/_toast's `data: { controller: … }` adds it beside), which owns the
// look and lifecycle; this controller only records, server-side, that the
// agent SAW the toast, so it never comes back on any browser or device.
//
// "Seen" means an explicit act (owner, 2026-10-06: "make sure that the toast is
// seen"): the close button, Escape (gl-toast's keyboard dismiss), or the Read
// action (the element marked data-acknowledge-toast-read). Merely rendering is
// not seen: until one of those happens the server keeps rendering the toast on
// every page load and sign-in.
//
// The Read action takes one of three shapes:
//   - a link opening a NEW TAB (the blog post): left or middle click records
//     seen and dismisses the toast; the page stays.
//   - a same-tab link (a staff message's own same-origin link_path): the
//     navigation waits for the acknowledgement so leaving the page cannot
//     abort it.
//   - a button with readEvent set (a staff message with no link): records seen,
//     then dispatches `readEvent` on window with `readEventDetail` — the
//     notifications bell listens and opens on that message's tab. Dispatched
//     after the PATCH settles so the bell lists the message as read.
//
// readUrl (optional) is where the Read action records seen instead of
// acknowledgeUrl, for a toast that tells the two apart — the promo toast's
// "Order Now" (via=ordered) versus closing it (via=closed). Only the first
// acknowledgement is ever sent, whichever it is.
//
// The PATCH never raises. If it fails, the toast simply returns on the next
// page load — the safe direction for a "make sure it is seen" rule.
export default class extends Controller {
  static values = { acknowledgeUrl: String, readUrl: String, readEvent: String, readEventDetail: Object }

  connect() {
    this.element.addEventListener("click", this.onClick)
    this.element.addEventListener("auxclick", this.onAuxClick)
    this.element.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
    this.element.removeEventListener("auxclick", this.onAuxClick)
    this.element.removeEventListener("keydown", this.onKeydown)
  }

  onClick = (event) => {
    if (event.target.closest(".gl-toast-close")) {
      this.acknowledge()
      return
    }
    const read = event.target.closest("[data-acknowledge-toast-read]")
    if (read) this.read(event, read)
  }

  // Middle-click opens a link in a new tab.
  onAuxClick = (event) => {
    if (event.button === 1 && event.target.closest("a[data-acknowledge-toast-read]")) {
      this.acknowledge(this.readAcknowledgeUrl)
      this.dismissToast()
    }
  }

  onKeydown = (event) => {
    if (event.key === "Escape") this.acknowledge()
  }

  read(event, element) {
    const acknowledged = this.acknowledge(this.readAcknowledgeUrl)
    this.dismissToast()

    if (element.tagName === "A") {
      if (this.opensSameTab(event, element)) {
        event.preventDefault()
        const href = element.getAttribute("href")
        acknowledged.then(() => this.visit(href))
      }
    } else if (this.readEventValue) {
      acknowledged.then(() => {
        window.dispatchEvent(new CustomEvent(this.readEventValue, { detail: this.readEventDetailValue }))
      })
    }
  }

  // A plain left click on a link without target=_blank replaces this page.
  // Modified clicks (new tab / window) leave the page alone.
  opensSameTab(event, link) {
    if (link.getAttribute("target") === "_blank") return false
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
  }

  get readAcknowledgeUrl() {
    return this.readUrlValue || this.acknowledgeUrlValue
  }

  // Returns a promise that always resolves, once the PATCH settles.
  acknowledge(url = this.acknowledgeUrlValue) {
    if (this.acknowledgement) return this.acknowledgement
    if (!url) return Promise.resolve()

    this.acknowledgement = patch(url, { responseKind: "json" }).catch(() => {})
    return this.acknowledgement
  }

  visit(href) {
    window.location.assign(href)
  }

  dismissToast() {
    this.application.getControllerForElementAndIdentifier(this.element, "gl-toast")?.dismiss()
  }
}
